import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import "./StudentView.css";
import { filterAssignmentsFunc, findClassroom } from "../TeacherHomeScreen/FilteringFromURLFunctions";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/configureStore";
import ReadleeProfile from "../../../../services/firebase/ReadleeProfile";
import FirebaseService from "../../../../services/firebase/FirebaseService";
import Submission from "../../../../interfaces/Submission";
import ClassroomReadingStats from "../../../../interfaces/ClassroomReadingStats";
import AssignmentOnTeacher from "../../../../state/teacherData/AssignmentOnTeacher";
import { formatLastLogin, formatTime, sortStrings, sortNumbers } from "../Table/TableSortAndFormatFunctions";
import accuracyToBand, { accuracyToColor } from "../../../../utils/AccuracyToBand";
import Table from "../Table/Table";
import Loading from "../../../Loading/Loading";
import { MenuItem, Select, FormControl, InputLabel, Typography, Divider } from "@material-ui/core";
import { NavLink } from "react-router-dom";
import EmptyState from "../../../EmptyState";
import FilterIcon from "../../../../assets/icons/Filter_By_Class_Icon_orange.svg";
import { PageLabel } from "../../../PageLabel";

const StudentView = () => {
  const history = useHistory();

  // Get attributes classId and studentId from url
  const { classId, studentId } = useParams<{ classId: string; studentId: string }>();

  // Get teacher data from store
  const profile = useSelector((state: RootState) => state.currentUserState.currentUser);
  const classrooms = useSelector((state: RootState) => state.teacherState.classrooms);
  const students = useSelector((state: RootState) => state.teacherState.students);
  const assignments = useSelector((state: RootState) => state.teacherState.assignments);
  const starredAssignments = useSelector((state: RootState) => state.teacherState.starredAssignments);
  const assignmentClasses = useSelector((state: RootState) => state.teacherState.assignmentClasses);

  const filterAssignments = useMemo(
    () => filterAssignmentsFunc(assignments, assignmentClasses, starredAssignments, profile),
    [assignments, assignmentClasses, starredAssignments, profile],
  );

  // filter teacher data to get applicable classroom, students, and assignments
  const classroom = findClassroom(classrooms, classId);
  const student = students[studentId] ?? undefined;
  const studentClasses = useMemo(
    () => classrooms.filter(classroom => classroom.students?.[studentId] === true),
    [classrooms, studentId],
  );
  const assignmentsInClass = useMemo(() => filterAssignments(undefined, classId), [filterAssignments, classId]);
  const studentAssignmentsInClass = useMemo(
    () =>
      assignmentsInClass?.filter(
        assignment => assignmentClasses[assignment.id]?.[classId]?.students?.[studentId] === true,
      ),
    [assignmentsInClass, assignmentClasses, classId, studentId],
  );

  const [studentSummary, setStudentSummary] = useState<{
    profile?: ReadleeProfile;
    classroomReadingStats?: ClassroomReadingStats;
    submissions?: number[];
  }>({});

  const [studentAssignments, setStudentAssignments] = useState<
    { assignment: AssignmentOnTeacher; submission: Submission }[]
  >([]);

  useEffect(() => {
    if (student && studentAssignmentsInClass) {
      const getStudentData = async (student: ReadleeProfile) => {
        const classroomReadingStats = await FirebaseService.getUserClassroomReadingStats(student.uid, classId);
        const totalSubmissions =
          classroomReadingStats && classroomReadingStats.numSubmissions ? classroomReadingStats.numSubmissions : 0;
        const submissionRatio = [
          totalSubmissions / (studentAssignmentsInClass.length ?? 0),
          totalSubmissions,
          studentAssignmentsInClass.length,
        ];
        // Set info for student summary table
        setStudentSummary({ profile: student, classroomReadingStats, submissions: submissionRatio });

        const allStudentSubmissions = await FirebaseService.getSubmissions(student.uid);
        const assignmentsWithSubmissions =
          studentAssignmentsInClass.map(assignment => {
            return {
              assignment,
              submission: allStudentSubmissions.find(
                submission => submission.book?.id === assignment.assignmentDetails.bookId,
              ) ?? {
                accuracy: 0,
                classId: classId,
                pagesRead: 0,
                readingStatus: "incomplete",
                totalTime: 0,
                totalWordsRead: 0,
                userId: student.uid,
                wordsPerMin: 0,
                turnedIn: false,
              },
            };
          }) ?? [];
        setStudentAssignments(assignmentsWithSubmissions);
      };
      getStudentData(student);
    }
  }, [classId, student, studentAssignmentsInClass]);

  // Change class function on dropdown menu selection
  const changeClass = (classId: string) => history.push(`/Class/${classId}/Student/${studentId}`);

  // Direct to submission view
  // "/Class/:classId/Student/:studentId/Assignment/:assignmentId"
  const createSubmissionLinkURL = (row: any) =>
    `/Class/${classId}/Student/${studentId}/Assignment/${row.original.assignment.id}`;

  // Columns for table
  const studentSummaryColumns = useMemo(
    () => [
      {
        // student column
        Header: "Student",
        disableSortBy: true,
        // if preregistered student (property email exists on student object), use email as name
        // if last name and first name don't exist on Readlee Profile, use display name
        accessor: (data: any): string =>
          data.profile.lastName && data.profile.firstName
            ? `${data.profile.lastName}, ${data.profile.firstName}`
            : `${data.profile.displayName}`,
      },
      {
        // assignment column
        Header: "Assignments",
        disableSortBy: true,
        // accessor is what determines sorting,
        // so use the decimal value of submissions divided by assignments
        // if data doesnt exist or number of total assignments assigned to student is 0 (which makes the decimal value NaN), use undefined
        accessor: (data: any) => (data.submissions && !isNaN(data.submissions[0]) ? data.submissions[0] : undefined),
        // cell is what displays in the table, so use 'submissions/total assignments' as a string
        Cell: ({ value, row }: any) =>
          // include check for zero
          value || value === 0 ? `${row.original.submissions[1]}/${row.original.submissions[2]}` : "",
      },
      {
        // wpm column
        Header: "WPM",
        disableSortBy: true,
        // accessor is what determines sorting, so use non-rounded value
        accessor: "classroomReadingStats.wordsPerMin",
        // cell is what displays in the table, so use WPM rounded to the nearest integer
        Cell: (props: any) =>
          // include check for zero
          props.value || props.value === 0 ? Math.round(props.value) : "",
      },
      {
        // accuracy column
        Header: "Accuracy",
        disableSortBy: true,
        accessor: "classroomReadingStats.accuracy",
        // cell is what displays in the table, so put svg circle here
        // use accuracyToColor to get the appropriate color
        Cell: (props: any) =>
          props.value || props.value === 0 ? (
            <div className="accuracy-container">
              <svg height="16" width="16">
                <circle cx="8" cy="8" r="8" fill={accuracyToColor(props.value)} />
              </svg>
              <div className="accuracy-popup">
                {accuracyToBand(props.value)}
                <div className="caret"></div>
              </div>
            </div>
          ) : (
            ""
          ),
      },
      {
        // total words column
        Header: "Words",
        disableSortBy: true,
        accessor: "classroomReadingStats.totalWordsRead",
      },
      {
        // reading time column
        Header: "Reading Time",
        disableSortBy: true,
        accessor: "classroomReadingStats.totalTimeRead",
        // cell is what displays in the table, so use formatted time read
        Cell: (props: any) =>
          // if student data doesn't exist, return empty string
          // else return formatted time (include check for zero)
          props.value || props.value === 0 ? formatTime(props.value) : "",
      },
      {
        // last login column
        Header: "Last Login",
        disableSortBy: true,
        // accessor is what determines sorting, so use date objects or return undefined
        accessor: (data: any) =>
          // if log in exists, return value as date
          data.profile?.lastLoginDay && new Date(data.profile.lastLoginDay),
        // cell is what displays in the table, so use formatted last login
        Cell: formatLastLogin,
      },
      {
        // grade column
        Header: "Grade",
        disableSortBy: true,
        accessor: (data: any) => {
          return data.profile?.grade?.[0] ?? "-";
        },
      },
    ],
    [],
  );

  const studentAssignmentsColumns = useMemo(
    () => [
      {
        // assignment column
        Header: "Assignments",
        accessor: "assignment.assignmentDetails.title",
        sortType: sortStrings,
      },
      {
        // completion % column
        Header: "Completion",
        accessor: (data: any) => data.submission.completionPercentage,
        sortType: sortNumbers,
        Cell: ({ value }: any) => (isNaN(value) ? "N/A" : Math.trunc(value * 100).toString() + "%"),
      },
      {
        // wpm column
        Header: "WPM",
        accessor: (data: any) => data.submission.wordsPerMin,
        sortType: sortNumbers,
        Cell: (props: any) => (isNaN(props.value) ? "N/A" : Math.trunc(props.value)),
      },
      {
        // accuracy column
        Header: "Accuracy",
        accessor: (data: any) => data.submission.accuracy,
        sortType: sortNumbers,
        // cell is what displays in the table, so put svg circle here
        // use accuracyToColor to get the appropriate color
        Cell: (props: any) =>
          props.value || props.value === 0 ? (
            <div className="accuracy-container">
              <svg height="16" width="16">
                <circle cx="8" cy="8" r="8" fill={accuracyToColor(props.value)} />
              </svg>
              <div className="accuracy-popup">
                {accuracyToBand(props.value)}
                <div className="caret"></div>
              </div>
            </div>
          ) : (
            ""
          ),
      },
      {
        // reading time column
        Header: "Minutes",
        accessor: (data: any) => data.submission.totalTime,
        sortType: sortNumbers,
        // cell is what displays in the table, so use formatted time read
        Cell: (props: any) => {
          // if student data doesn't exist, return empty string
          // else return formatted time (include check for zero)
          if (isNaN(props.value)) {
            return "N/A";
          }
          const mins = Math.trunc(props.value / 60);
          const seconds = Math.trunc(props.value % 60);
          return `${mins}:${seconds < 10 ? "0" + seconds.toString() : seconds}`;
        },
      },
      {
        // total words column
        Header: "Words",
        accessor: (data: any) => data.submission.totalWordsRead,
        sortType: sortNumbers,
        Cell: (props: any) => props.value ?? "N/A",
      },
      {
        // last login column
        Header: "Turned In",
        // If assignment turned in, return time of submission
        accessor: (data: any) => (data.submission.turnedIn ? data.submission.timeSubmitted : undefined),
        // sortType: sortDates,
        Cell: formatLastLogin,
      },
    ],
    [],
  );

  // check if class exists
  if (!classroom) {
    return <p>Classroom does not exist.</p>;
  }

  // check if student exists
  if (!student) {
    return <p>Student does not exist.</p>;
  }

  // check if student in class
  if (!classroom?.students[studentId]) {
    return <p>Student not in class.</p>;
  }

  return (
    <div className="StudentView">
      <PageLabel label="Student Profile">
        <NavLink className="reportHeader-backButton" to={`/Class/${classId}`}>
          Back to {classroom.className}
        </NavLink>
      </PageLabel>
      <Typography variant="h3" className="studentHeader">{`${student.firstName} ${student.lastName}`}</Typography>
      <Divider />
      <br />
      <br />
      {studentClasses.length === 0 ? (
        ""
      ) : (
        <>
          <FormControl variant="outlined">
            <InputLabel>
              <img className="FilterIcon" src={FilterIcon} aria-hidden alt="" />
              Filter By Class
            </InputLabel>
            {/* label dictates the length of the gap in the outline for when the label moves up */}
            {/* the 'xxl' is a stand in for the icon */}
            <Select
              label="xxlFilter By Class"
              onChange={val => changeClass(val.target.value as string)}
              value={classId}
              disableUnderline={true}
            >
              {studentClasses.length &&
                studentClasses.map(classroom => <MenuItem value={classroom.id}>{classroom.className}</MenuItem>)}
            </Select>
          </FormControl>
        </>
      )}
      <br />
      <br />
      {!studentSummary.hasOwnProperty("profile") ? (
        <Loading />
      ) : (
        <Table kind="student" columns={studentSummaryColumns} data={[studentSummary]} />
      )}

      <div>
        <h2>Assignments</h2>

        {studentAssignmentsInClass.length === 0 ? (
          <EmptyState isClassPage>No Assignments Assigned to Student</EmptyState>
        ) : studentAssignments.length !== studentAssignmentsInClass.length ? (
          <Loading />
        ) : (
          <Table
            kind="assignment"
            columns={studentAssignmentsColumns}
            data={studentAssignments}
            createRowLinkURL={createSubmissionLinkURL}
          />
        )}
      </div>
    </div>
  );
};

export default StudentView;
